import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';

import {
  DiscountTypes,
  DiscountUserMetricsTypes,
} from '../services/ocpp/types';
import { ocppService } from '../services/ocpp/OcppService';
import { useUser } from '../hooks/useUser';
import { UserStatus } from '../common/enums/auth';

export type DiscountRange = {
  id?: string;
  _id?: string;
  value: number;
  label: number;
  discount?: number;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  min?: number;
  max?: number | null;
  discountLabel?: string;
};

type DiscountContextType = {
  marks: DiscountRange[];
  values: number[];
  maxRangeValue: number;
  marksFromDB: DiscountRange[];
  currentUserDiscountRange: DiscountUserMetricsTypes | null;
  discountRanges: DiscountUserMetricsTypes[] | null;
};

type DiscountActionContextType = {
  addDiscount: () => void;
  changeDiscountRangeValue: (newValues: number[], activeThumb: number) => void;
  setDiscountValues: (data: DiscountTypes[]) => void;
  setMaxRangeValue: Dispatch<SetStateAction<number>>;
  deleteDiscount: (row: DiscountRange) => void;
  updateDiscountValue: (row: DiscountRange, newDiscountValue: number) => void;
  setMarksFromDB: Dispatch<SetStateAction<DiscountRange[]>>;
  setCurrentUserDiscountRange: Dispatch<
    SetStateAction<DiscountUserMetricsTypes | null>
  >;
  setDiscountRanges: Dispatch<
    SetStateAction<DiscountUserMetricsTypes[] | null>
  >;
  updateDiscountLabel: (row: DiscountRange, newDiscountLabel: string) => void;
};

export const DiscountContext = createContext<DiscountContextType | undefined>(
  undefined,
);
export const DiscountActionContext =
  createContext<DiscountActionContextType | null>(null);

export const DiscountProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [maxRangeValue, setMaxRangeValue] = useState<number>(1000);
  const [marks, setMarks] = useState<DiscountRange[]>([]);
  const [values, setValues] = useState<number[]>([]);
  const [marksFromDB, setMarksFromDB] = useState<DiscountRange[]>([]);
  const [currentUserDiscountRange, setCurrentUserDiscountRange] =
    useState<DiscountUserMetricsTypes | null>(null);
  const [discountRanges, setDiscountRanges] = useState<
    DiscountUserMetricsTypes[] | null
  >(null);
  const { userStatus } = useUser();

  const addDiscount = useCallback(() => {
    const lastMark = marks[marks.length - 1].value;
    const secondLastMark = marks[marks.length - 2].value + 1;

    const newDiscount = Math.floor(
      Math.random() * (lastMark - secondLastMark) + secondLastMark,
    );

    const copiedMarks = [...marks];
    let lastDiscountRange = copiedMarks.pop();
    const newDiscountRange: DiscountRange = {
      label: (newDiscount * maxRangeValue) / 100,
      value: newDiscount,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      discount: 0,
      max: (newDiscount * maxRangeValue) / 100,
      min: copiedMarks[copiedMarks.length - 1].max! + 1,
    };
    copiedMarks.push(newDiscountRange);
    if (lastDiscountRange) {
      lastDiscountRange = {
        ...lastDiscountRange,
        min: (newDiscount * maxRangeValue) / 100 + 1,
        updatedAt: new Date().toISOString(),
      };
      copiedMarks.push(lastDiscountRange);
    }

    setMarks(copiedMarks);
    setValues((prev) => [...prev, newDiscount]);
  }, [marks]);

  const deleteDiscount = useCallback(
    (row: DiscountRange) => {
      const isExistedMarks = marks.find((item) => item.id === row.id);
      if (isExistedMarks) {
        const filteredMarks = marks.filter((item) => item.id !== row.id);

        updateMarksAfterDeleteRange(filteredMarks, setMarks, setValues);
      } else {
        const filteredMarks = marks.filter((item) => {
          if (item.max !== row.max && item.min !== row.min) return true;
          return false;
        });

        updateMarksAfterDeleteRange(filteredMarks, setMarks, setValues);
      }
    },
    [marks],
  );

  const changeDiscountRangeValue = useCallback(
    (newValues: number[], activeThumb: number) => {
      setValues(newValues);
      setMarks((prev) => {
        return prev.map((item, index) => {
          if (index === activeThumb + 1) {
            prev[index + 1].min =
              (newValues[activeThumb] * maxRangeValue) / 100;
            return {
              ...item,
              max: (newValues[activeThumb] * maxRangeValue) / 100,
            };
          }
          return item;
        });
      });
    },
    [maxRangeValue],
  );

  const updateDiscountLabel = useCallback(
    (row: DiscountRange, newDiscountLabel: string) => {
      const updatedMarks = marks.map((item) => {
        if (item.id === row.id) {
          return {
            ...item,
            discountLabel: newDiscountLabel,
          };
        } else return item;
      });
      setMarks(updatedMarks);
    },
    [marks],
  );

  const updateDiscountValue = useCallback(
    (row: DiscountRange, newDiscountValue: number) => {
      const isExistedMarks = marks.find((item) => item.id === row.id);
      let updatedMarks;
      if (isExistedMarks) {
        updatedMarks = marks.map((item) => {
          if (item.id === row.id) {
            return {
              ...item,
              discount: newDiscountValue,
            };
          } else return item;
        });
      } else {
        updatedMarks = marks.map((item) => {
          if (item.max === row.max && item.min === row.min)
            return { ...item, discount: newDiscountValue };
          return item;
        });
      }
      setMarks(updatedMarks);
    },
    [marks],
  );

  const setDiscountValues = useCallback(
    (data: DiscountTypes[]) => {
      const { rangesMarks, setRangeValues } = parseData(data, maxRangeValue);

      setValues(setRangeValues.filter((item) => item !== undefined));
      setMarks(rangesMarks);
    },
    [maxRangeValue],
  );

  useEffect(() => {
    if (marks.length) {
      const updatedMarks = marks.map((mark) => {
        if (mark.max) {
          const updatedValue = (mark.max * 100) / maxRangeValue;
          return {
            ...mark,
            value: updatedValue,
            label: mark.max,
          };
        }
        return mark;
      });
      const setRangeValues = updatedMarks.map((item, index) => {
        if (index !== 0 && index !== updatedMarks.length - 1) {
          return item.value;
        }
      });

      setValues(setRangeValues.filter((item) => item !== undefined));
      setMarks(updatedMarks);
    }
  }, [maxRangeValue]);

  const fetchUserMetrics = async () => {
    try {
      const response = await ocppService.getDiscountUserMetrics();
      response.forEach((userMetric) => {
        if (userMetric?.current) {
          setCurrentUserDiscountRange(userMetric);
        }
      });
      setDiscountRanges(response);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    if (userStatus !== UserStatus.ACTIVE) return;
    fetchUserMetrics();
  }, [userStatus]);

  const value = useMemo(
    () => ({
      marks,
      values,
      maxRangeValue,
      marksFromDB,
      currentUserDiscountRange,
      discountRanges,
    }),
    [
      marks,
      values,
      maxRangeValue,
      marksFromDB,
      currentUserDiscountRange,
      discountRanges,
    ],
  );

  const actions = useMemo(
    () => ({
      addDiscount,
      changeDiscountRangeValue,
      setDiscountValues,
      setMaxRangeValue,
      deleteDiscount,
      updateDiscountValue,
      setMarksFromDB,
      setCurrentUserDiscountRange,
      setDiscountRanges,
      updateDiscountLabel,
    }),
    [
      addDiscount,
      changeDiscountRangeValue,
      setDiscountValues,
      setMaxRangeValue,
      deleteDiscount,
      updateDiscountValue,
      setMarksFromDB,
      setCurrentUserDiscountRange,
      setDiscountRanges,
      updateDiscountLabel,
    ],
  );

  return (
    <DiscountContext.Provider value={value}>
      <DiscountActionContext.Provider value={actions}>
        {children}
      </DiscountActionContext.Provider>
    </DiscountContext.Provider>
  );
};

const parseData = (data: DiscountTypes[], maxRangeValue: number) => {
  const sortedDiscount = [...data].sort((a, b) => {
    if (a.max === null) return 1;
    if (b.max === null) return -1;
    return a.max - b.max;
  });

  const startRangeValues: DiscountRange = { value: 0, label: 0, min: 1 };
  const lastRangeValues: DiscountRange = {
    value: 100,
    label: maxRangeValue,
  };

  const rangesMarks = [];
  rangesMarks.push(startRangeValues);
  sortedDiscount.forEach((item, index) => {
    if (item.max) {
      const itemRange: DiscountRange = {
        id: item._id,
        value:
          index === 0
            ? maxRangeValue / item.max
            : (item.max * 100) / maxRangeValue,
        label: item.max,
        discount: item.discount,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        max: item.max,
        min: item.min,
        discountLabel: item.discountLabel,
        // id: data._id,
      };
      rangesMarks.push(itemRange);
    }
  });
  rangesMarks.push({
    ...lastRangeValues,
    min: rangesMarks[rangesMarks.length - 1].max!,
    max: null,
    discount: sortedDiscount[sortedDiscount.length - 1].discount,
    createdAt: sortedDiscount[sortedDiscount.length - 1].createdAt,
    updatedAt: sortedDiscount[sortedDiscount.length - 1].updatedAt,
    discountLabel: sortedDiscount[sortedDiscount.length - 1].discountLabel,
    id: sortedDiscount[sortedDiscount.length - 1]._id,
  });
  const setRangeValues = updateValueAfterUpdateMarks(rangesMarks);

  return {
    setRangeValues,
    rangesMarks,
  };
};

const updateValueAfterUpdateMarks = (marks: DiscountRange[]) => {
  return marks.map((item, index) => {
    if (index !== 0 && index !== marks.length - 1) {
      return item.value;
    }
  });
};

const updateMarksAfterDeleteRange = (
  marks: DiscountRange[],
  setMarks: React.Dispatch<React.SetStateAction<DiscountRange[]>>,
  setValues: React.Dispatch<React.SetStateAction<number[]>>,
) => {
  const lastMaxDiscountValue = marks[marks.length - 2].max;

  marks[marks.length - 1].min = lastMaxDiscountValue! + 1;
  const setRangeValues = updateValueAfterUpdateMarks(marks);
  setMarks(marks);
  setValues(setRangeValues.filter((item) => item !== undefined));
};
